import React, { useState } from 'react'
import { Loading } from '@pandiafi/design-system'

import type { TimeSlotForResident } from '../../../common/validations/reservationCalendarSchemas'
import { useAPIGetMultipleReservationCalendarTimeSlots } from '../../api/apiHooks/useAPIGetMultipleReservationCalendarTimeSlots'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { ReservationCalendarGrid } from './ReservationCalendarGrid'
import { ReservationModal } from './ReservationModal'
import type { ReservationCalendarForResident, PriceInfoForDate } from './types'
import { Contract } from '../../../common/validations'

interface ReservationCalendarsWithLoadingProps {
  calendars: ReservationCalendarForResident[]
  startDate: string
  endDate: string
  contract: Contract
  readonly?: boolean
}

export const ReservationCalendarsWithLoading: React.FC<
  ReservationCalendarsWithLoadingProps
> = ({ calendars, startDate, endDate, contract, readonly = false }) => {
  const { currentContractId } = useCurrentContract()

  const calendarIds = calendars.map((calendar) => String(calendar.id))

  const queries = useAPIGetMultipleReservationCalendarTimeSlots(
    currentContractId,
    calendarIds,
    startDate,
    endDate
  )

  const isAnyLoading = queries.some((query) => query.isLoading)
  const timeSlotsByCalendarId = new Map(
    calendarIds.flatMap((calendarId, index) =>
      queries[index]?.data ? [[calendarId, queries[index].data]] : []
    )
  )

  const [selectedCalendar, setSelectedCalendar] =
    useState<ReservationCalendarForResident | null>(null)
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<
    TimeSlotForResident[]
  >([])
  const [selectedPriceInfo, setSelectedPriceInfo] = useState<
    PriceInfoForDate | undefined
  >(undefined)

  const closeModal = () => {
    setSelectedCalendar(null)
    setSelectedTimeSlots([])
    setSelectedPriceInfo(undefined)
  }

  const handleTimeSlotSelection = (
    calendar: ReservationCalendarForResident,
    timeSlots: TimeSlotForResident[],
    priceInfo: PriceInfoForDate
  ) => {
    if (readonly) return
    setSelectedCalendar(calendar)
    setSelectedTimeSlots(timeSlots)
    setSelectedPriceInfo(priceInfo)
  }

  if (isAnyLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loading variant="details" />
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-12">
        {calendars.map((calendar) => (
          <ReservationCalendarGrid
            key={calendar.id}
            calendar={calendar}
            startDate={startDate}
            endDate={endDate}
            onTimeSlotSelection={(timeSlots, priceInfo) =>
              handleTimeSlotSelection(calendar, timeSlots, priceInfo)
            }
            timeSlotsData={timeSlotsByCalendarId.get(String(calendar.id))}
            contract={contract}
            selectedTimeSlotsId={selectedTimeSlots.map((s) => s.id)}
            readonly={readonly}
          />
        ))}
      </div>

      <ReservationModal
        calendar={selectedCalendar}
        timeSlots={selectedTimeSlots}
        priceInfo={selectedPriceInfo}
        onClose={closeModal}
      />
    </>
  )
}
